/**
 * ReviewPanel
 *
 * Side panel (Sheet) for reviewing content details.
 * Shows full content preview and approve/reject actions.
 *
 * Part of Story 29: Content Review Dashboard with Reviewer Attribution
 *
 * Date: December 16, 2025
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { toast } from '@/components/ui/use-toast';
import {
  FileText,
  Tag,
  FolderTree,
  CheckCircle,
  XCircle,
  Loader2,
  Calendar,
  User,
  Globe,
  RotateCcw,
  Sparkles,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Lightbulb,
  BookOpen,
  ThumbsUp,
  ThumbsDown,
  ExternalLink,
  History,
  GitCompare,
  TrendingUp,
  MinusCircle,
  Target,
  Database,
  Bug,
  Zap,
  AlertCircle,
  Pencil,
} from 'lucide-react';
import { format } from 'date-fns';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Progress } from "@/components/ui/progress";
import {
  QueueItem,
  ContentDetails,
  ContentAnalysis,
  KnowledgeRetrievalSignal,
  ContentIssue,
  ImprovementAction,
  SavedEvaluation,
  EvaluationHistoryItem,
  ResolutionResult,
  RecommendedSolution,
  DeploymentVerificationResult,
  getContentDetails,
  approveContent,
  rejectContent,
  updateRejectionReason,
  resetToPending,
  analyzeContent,
  createKnowledgeProposals,
  getContentTypeLabel,
  getIssueTypeLabel,
  getSavedEvaluation,
  saveEvaluation,
  takeIssueAction,
  getSuggestedActionForIssue,
  hasActionBeenTaken,
  getEvaluationHistory,
  checkIssueResolution,
  getAllEvaluations,
  EvaluationStats,
  verifyDeployment,
  getFaqStatusLabel,
  getFaqStatusColor,
} from '@/services/contentReviewService';
import type { RejectKind } from '@/services/contentReviewService';
import { useSite } from '@/contexts/SiteContext';
import { useAuth } from '@/context/auth-context';
import { useSearchParams } from 'react-router-dom';
import { CategoryAEOViewer } from '@/components/aeo/CategoryAEOViewer';
import { ProductAEOViewer } from '@/components/aeo/ProductAEOViewer';
import { AttributeSourceSummary } from './AttributeSourceSummary';
import { KnowledgeFromIssueModal } from './KnowledgeFromIssueModal';
import { HallucinationReportSection } from './HallucinationReportSection';
import { CoverageReportSection } from './CoverageReportSection';
import { PersonaValidationSection } from './PersonaValidationSection';
import { CoherenceValidationSection } from './CoherenceValidationSection';
import { CompletenessValidationSection } from './CompletenessValidationSection';
import { RegenerationControl } from './RegenerationControl';
import { useRegenerationQueueObserver } from './useRegenerationQueueObserver';

interface ReviewPanelProps {
  open: boolean;
  onClose: () => void;
  item: QueueItem | null;
  onActionComplete: () => void;
}

function getContentTypeIcon(type: string) {
  switch (type) {
    case 'ai_article':
      return <FileText className="h-5 w-5 text-blue-500" />;
    case 'product_aeo':
      return <Tag className="h-5 w-5 text-green-500" />;
    case 'category_aeo':
      return <FolderTree className="h-5 w-5 text-purple-500" />;
    default:
      return <FileText className="h-5 w-5" />;
  }
}

/**
 * Get the follow-up location for an action type
 */
function getFollowUpLocation(actionType: string): { path: string; label: string } | null {
  switch (actionType) {
    case 'SEND_SUGGESTION':
    case 'FLAG_BUG':
      return { path: '/admin/suggestions', label: 'View in Platform Suggestions' };
    case 'CREATE_DATA_ISSUE':
      return { path: '/research/data-quality-audit', label: 'View in Data Quality Audit' };
    case 'TRIGGER_RESEARCH':
      return { path: '/domain-knowledge', label: 'View in Domain Knowledge' };
    default:
      return null;
  }
}

/**
 * Clean HTML content that may be wrapped in markdown code fences
 * and/or contain a full HTML document structure.
 * Extracts just the body content for rendering.
 */
function cleanHtmlContent(html: string): string {
  if (!html) return '';

  let cleaned = html;

  // Remove markdown code fences (```html ... ```)
  cleaned = cleaned.replace(/^```html\s*/i, '').replace(/```\s*$/, '');

  // Try to extract body content if it's a full HTML document
  const bodyMatch = cleaned.match(/<body[^>]*>([\s\S]*)<\/body>/i);
  if (bodyMatch) {
    cleaned = bodyMatch[1];
  }

  // If no body tag, try to skip DOCTYPE and html/head sections
  if (!bodyMatch) {
    // Remove DOCTYPE
    cleaned = cleaned.replace(/<!DOCTYPE[^>]*>/i, '');
    // Remove <html> and </html>
    cleaned = cleaned.replace(/<\/?html[^>]*>/gi, '');
    // Remove entire <head>...</head>
    cleaned = cleaned.replace(/<head[\s\S]*?<\/head>/gi, '');
  }

  // Remove any stray <title> and <meta> tags that may appear outside head
  cleaned = cleaned.replace(/<title[^>]*>[\s\S]*?<\/title>/gi, '');
  cleaned = cleaned.replace(/<meta[^>]*\/?>/gi, '');

  // Remove any leading/trailing whitespace and empty lines
  cleaned = cleaned.replace(/^\s+/, '');

  return cleaned.trim();
}

export function ReviewPanel({
  open,
  onClose,
  item,
  onActionComplete
}: ReviewPanelProps) {
  const { selectedSite } = useSite();
  const { user } = useAuth();
  const observeRegeneration = useRegenerationQueueObserver(user?.id || '', selectedSite?.id || '', onActionComplete);
  const [content, setContent] = useState<ContentDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [flagAsDataIssue, setFlagAsDataIssue] = useState(false);
  // task-edbnz2qj5c: for a PUBLISHED product page the reject is a choice between two named
  // pull-backs, not a checkbox — 'content' (regenerate, nothing recorded against the product) is
  // the default, 'data_quality' records a data issue as the checkbox always did.
  const [publishedRejectKind, setPublishedRejectKind] = useState<RejectKind>('content');
  const [error, setError] = useState<string | null>(null);

  // Rejection reason editing state
  const [editingRejection, setEditingRejection] = useState(false);
  const [editedRejectionReason, setEditedRejectionReason] = useState('');
  const [editRejectionLoading, setEditRejectionLoading] = useState(false);

  // AI Analysis state
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [analysis, setAnalysis] = useState<ContentAnalysis | null>(null);
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  const [analysisExpanded, setAnalysisExpanded] = useState(true);
  // ⛔ Tracked SEPARATELY from `analysis` on purpose: the verdict and the question of whether it was
  // grounded are two facts, and every place that collapsed them is what this fixes.
  const [knowledgeRetrieval, setKnowledgeRetrieval] = useState<KnowledgeRetrievalSignal | null>(null);
  const [creatingProposals, setCreatingProposals] = useState(false);
  const [createdProposals, setCreatedProposals] = useState<Array<{ id: string; title: string }>>([]);

  // Persisted evaluation state
  const [savedEvaluation, setSavedEvaluation] = useState<SavedEvaluation | null>(null);
  const [issueActions, setIssueActions] = useState<ImprovementAction[]>([]);
  const [actionInProgress, setActionInProgress] = useState<string | null>(null);

  // Quality evolution state (Story 46)
  const [evaluationHistory, setEvaluationHistory] = useState<EvaluationHistoryItem[]>([]);
  const [previousEvaluation, setPreviousEvaluation] = useState<EvaluationHistoryItem | null>(null);
  const [resolutionCheckLoading, setResolutionCheckLoading] = useState(false);
  const [resolutionResults, setResolutionResults] = useState<{
    resolutions: ResolutionResult[];
    overallImprovement: string;
    remainingConcerns: string[];
    summary: { total: number; resolved: number; partiallyResolved: number; notResolved: number };
  } | null>(null);
  const [showResolutionPanel, setShowResolutionPanel] = useState(false);
  const [evaluationStats, setEvaluationStats] = useState<EvaluationStats | null>(null);
  const [showQualityEvolution, setShowQualityEvolution] = useState(false);

  // Story 48: Knowledge from issue modal state
  const [knowledgeModalOpen, setKnowledgeModalOpen] = useState(false);
  const [knowledgeModalIssue, setKnowledgeModalIssue] = useState<ContentIssue | null>(null);

  const [knowledgeModalIssueIndex, setKnowledgeModalIssueIndex] = useState<number>(0);

  // Deployment verification state
  const [verifying, setVerifying] = useState(false);
  const [verificationResult, setVerificationResult] = useState<DeploymentVerificationResult | null>(null);

  // Language selector state (product_aeo only). Init from URL ?language= so a
  // shared link opens in the chosen language; otherwise null = backend default
  // (source language).
  // ⛔ Bumped on every item/language change; each async loader captures it and refuses to write state
  // if it no longer matches. A ref because the guard must be visible to a closure from a previous
  // render and must change synchronously.
  const loadGenerationRef = useRef(0);

  /**
   * ⛔⛔ TRUE IF THE ITEM/LANGUAGE CHANGED WHILE AN await WAS PENDING. Call it after EVERY suspension
   * point, before doing anything item-scoped.
   *
   * ⛔⛔ "ITEM-SCOPED" MEANS STATE **OR EFFECT**, AND GETTING THAT AXIS WRONG COST A REVIEW ROUND.
   * An earlier version of this comment classified on "writes item-scoped STATE after an await" and
   * therefore listed handleApprove/handleReject/handleResetToPending as DELIBERATELY NOT GUARDED —
   * "they call onActionComplete()/onClose(), whose purpose is to affect the parent". By that
   * predicate the exclusion was correct: they write no content state. But they perform item-scoped
   * EFFECTS — a toast naming the item, a dialog reset, and `onClose()`, which closes whatever panel
   * the operator is looking at NOW, not the one whose mutation finished.
   * 🔑 THE ENUMERATION WAS COMPLETE AND THE PREDICATE WAS ONE CATEGORY NARROW. The list was never
   * the fragile part — the definition of the property being listed was. (offering-dev's reading,
   * and it is the right one.)
   *
   * ⇒ ALL 13 ASYNC PATHS THAT await ARE NOW GUARDED. There is no exclusion list to keep in sync,
   * which is deliberate: an exclusion list is a standing claim that something is safe, and it is the
   * kind of claim nobody re-derives.
   *
   * ✅ THE ONE THING THAT IS DELIBERATELY UNGUARDED IS A STATEMENT, NOT A FUNCTION:
   * `onActionComplete()` — the mutation really happened, so the parent's queue row is stale
   * regardless of what is on screen. In every handler it is called FIRST, before the guard, so that
   * "unconditional" is structural rather than a promise.
   *
   * ⛔⛔ THE UNIT IS THE STATEMENT, NOT THE FUNCTION — three review rounds moved this boundary inward
   * and the last one moved it past where this comment was pointing:
   *     round A: guard the three LOADERS            -> missed the other async writers
   *     round B: guard all thirteen FUNCTIONS       -> missed catch/finally inside them
   *     round C: guard the lifecycle HANDLERS       -> missed one TOAST inside a handler already guarded
   * ⇒ So "which functions" was never the question. THE QUESTION IS WHICH STATEMENTS AFTER AN await
   * ARE ITEM-SCOPED, and the answer is: everything that names the item or touches the screen — state
   * writes, toasts, dialog resets, onClose(). `onActionComplete()` is the single deliberate
   * exception, and it is placed FIRST in every handler so that its exemption is structural rather
   * than remembered.
   * ⚠️ A guarded function is not a safe function. Adding an await to an already-guarded handler adds
   * a new suspension point, and every statement after it needs the check again.
   * (offering-dev's reading: each round the boundary went function -> handler -> statement.)
   */
  const isStaleLoad = (generation: number) => generation !== loadGenerationRef.current;

  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedLanguage, setSelectedLanguage] = useState<string | null>(
    searchParams.get('language')
  );

  useEffect(() => {
    // ⛔⛔ EVERY ITEM/LANGUAGE CHANGE INVALIDATES EVERY REQUEST STARTED FOR THE PREVIOUS ONE.
    // Clearing state synchronously was only half a fix and I shipped it as if it were the whole one:
    // it fixed the SEQUENCING (stale values surviving a switch) and did nothing about the RACE. An
    // in-flight loadSavedEvaluation for item A resolves AFTER the switch and repopulates A's saved
    // evaluation, actions and rawAnalysis while B's content is on screen — so issue actions can be
    // written against the wrong evaluation id. Same race on content and history.
    // ⇒ A generation token, bumped here and captured by each loader, is the mechanism. `await`
    // cannot be un-awaited, so the only reliable guard is to check identity BEFORE writing state.
    // ⚠️ A ref, not state: it must be readable by a closure created in a PREVIOUS render, and it
    // must change synchronously — a state bump would not be visible to the request already running.
    loadGenerationRef.current += 1;
    const generation = loadGenerationRef.current;

    if (item && open) {
      // ⛔ CLEAR EVERY ITEM-SCOPED VALUE, NOT JUST THE VERDICT. `savedEvaluation`, `issueActions` and
      // the quality-history state were left standing, and the loaders only overwrite them when the
      // NEW item HAS records — so an item with no saved evaluation inherited the previous item's.
      // ⚠️ Both halves are needed and neither is sufficient: clearing without the token leaves the
      // race, and the token without clearing leaves the no-records case.
      setAnalysis(null);
      setKnowledgeRetrieval(null);
      setAnalysisError(null);
      // ⚠️ REQUIRED BY THE GUARD ABOVE, NOT INDEPENDENT OF IT. A superseded handleAnalyze no longer
      // clears the spinner in its finally (it belongs to the latest request), so without this reset
      // an item switch during an analysis would leave the new item spinning forever. The guard and
      // this line are one change; shipping either alone is a bug.
      setAnalysisLoading(false);
      // ⚠️ EVERY OPERATION SPINNER GUARDED IN A `finally` MUST BE RESET HERE. The guard stops a
      // superseded run from clearing a flag that now belongs to an operation on the NEW item; the
      // cost is that the superseded run no longer clears its OWN flag, so without these resets an
      // item switch mid-operation leaves the new item stuck. Guard and reset are one change.
      setResolutionCheckLoading(false);
      setEditRejectionLoading(false);
      setVerifying(false);
      setCreatingProposals(false);
      setActionInProgress(null);
      setActionLoading(false);
      setSavedEvaluation(null);
      setIssueActions([]);
      setEvaluationHistory([]);
      setPreviousEvaluation(null);
      setEvaluationStats(null);
      setResolutionResults(null);
      setShowResolutionPanel(false);
      setShowQualityEvolution(false);
      setVerificationResult(null);
      setCreatedProposals([]);
      loadContentDetails(generation);
      loadSavedEvaluation(generation);
      loadEvaluationHistory(generation);
    } else {
      setContent(null);
      setError(null);
      // Reset analysis state
      setAnalysis(null);
      setKnowledgeRetrieval(null);
      setAnalysisError(null);
      setAnalysisExpanded(true);
      setCreatedProposals([]);
      // Reset evaluation state
      setSavedEvaluation(null);
      setIssueActions([]);
      // Reset quality evolution state
      setEvaluationHistory([]);
      setPreviousEvaluation(null);
      setResolutionResults(null);
      setShowResolutionPanel(false);
      setEvaluationStats(null);
      setShowQualityEvolution(false);
      // Reset verification state
      setVerificationResult(null);
    }
  }, [item, open, selectedLanguage]);

  const loadSavedEvaluation = async (generation: number) => {
    if (!item) return;

    try {
      const response = await getSavedEvaluation(item.contentType, item.id);
      // ⛔ THE ITEM MAY HAVE CHANGED WHILE THIS WAS IN FLIGHT. Writing here would attach item A's
      // evaluation id to item B's on-screen issues, which is how an action gets recorded against
      // the wrong evaluation.
      if (isStaleLoad(generation)) return;
      if (response.success && response.data) {
        setSavedEvaluation(response.data);
        setIssueActions(response.data.actions || []);
        // Also set the analysis from saved evaluation
        if (response.data.rawAnalysis) {
          setAnalysis(response.data.rawAnalysis);
        }
      }
    } catch (err) {
      console.log('[ReviewPanel] No saved evaluation found');
    }
  };

  // ⛔⛔ `generation` IS REQUIRED, DELIBERATELY — NO DEFAULT. A default of
  // `loadGenerationRef.current` re-reads the token AT CALL TIME, so a caller holding a captured
  // generation (and a closure over the OLD item) silently got the CURRENT one, its staleness check
  // passed, and it wrote the previous item's content into the new item's panel. That was the defect
  // codex-reviewer found at 15aea23f80, and the call site looked completely correct.
  // 🔑 A DEFAULT ARGUMENT THAT READS MUTABLE STATE IS INVISIBLE AT THE CALL SITE. Making the
  // parameter required moves the decision to where the caller can see it, and turns the whole class
  // into a compile error instead of a race.
  // ⚠️ It also removes an earlier trap: passed as a bare `onClick={loadContentDetails}` handler,
  // React supplied the click EVENT as `generation` and every response was discarded — a refresh
  // button that silently did nothing, failing in the direction that looks like "no data changed".
  const loadContentDetails = async (generation: number) => {
    if (!item) return;

    // CRITICAL: Require siteId for multi-tenancy security
    // Use item.siteId if available (from queue data), fall back to selectedSite
    const siteId = item.siteId || selectedSite?.id;
    if (!siteId) {
      console.error('[ReviewPanel] No siteId available for content details');
      setError('No site selected');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await getContentDetails(
        item.contentType,
        item.id,
        siteId,
        item.contentType === 'product_aeo' ? selectedLanguage : null
      );
      // ⛔ Superseded by a newer item/language selection — discard rather than render A into B.
      if (isStaleLoad(generation)) return;
      if (response.success) {
        setContent(response.data);
      }
    } catch (err) {
      if (isStaleLoad(generation)) return;
      console.error('[ReviewPanel] Error loading content:', err);
      setError(err instanceof Error ? err.message : 'Failed to load content');
    } finally {
      // ⚠️ The spinner is owned by the LATEST request. A superseded one clearing it would report the
      // current load as finished while it is still running.
      if (!isStaleLoad(generation)) setLoading(false);
    }
  };

  const handleLanguageChange = (language: string) => {
    setSelectedLanguage(language);
    // Keep id + type in URL alongside language so refresh + share still work
    const id = searchParams.get('id');
    const type = searchParams.get('type');
    const next: Record<string, string> = { language };
    if (id) next.id = id;
    if (type) next.type = type;
    setSearchParams(next);
  };

  const loadEvaluationHistory = async (generation: number) => {
    if (!item) return;

    try {
      // Load history for previous evaluation detection
      const response = await getEvaluationHistory(item.contentType, item.id, 5);
      if (isStaleLoad(generation)) return;
      if (response.success && response.data.length > 0) {
        setEvaluationHistory(response.data);
        // Set previous evaluation if there's at least one with issues
        const prevWithIssues = response.data.find(e => e.issuesFound && e.issuesFound.length > 0);
        if (prevWithIssues) {
          setPreviousEvaluation(prevWithIssues);
        }
      }

      // Load all evaluations with stats for quality evolution tracking
      const allEvalsResponse = await getAllEvaluations(item.contentType, item.id);
      // ⚠️ CHECKED AGAIN AFTER THE SECOND await, not only the first. One guard per suspension point:
      // the switch can land between the two requests just as easily as before either.
      if (isStaleLoad(generation)) return;
      if (allEvalsResponse.success && allEvalsResponse.data.stats) {
        setEvaluationStats(allEvalsResponse.data.stats);
      }
    } catch (err) {
      console.log('[ReviewPanel] No evaluation history found');
    }
  };

  const handleCheckResolution = async () => {
    if (!item || !previousEvaluation || !content) return;
    const generation = loadGenerationRef.current;

    setResolutionCheckLoading(true);
    setResolutionResults(null);

    try {
      const response = await checkIssueResolution(
        item.contentType,
        item.id,
        previousEvaluation.id,
        content.content as Record<string, unknown>
      );

      if (response.success) {
        if (isStaleLoad(generation)) return;
      setResolutionResults({
          resolutions: response.data.resolutions,
          overallImprovement: response.data.overallImprovement,
          remainingConcerns: response.data.remainingConcerns,
          summary: response.data.summary,
        });
        setShowResolutionPanel(true);

        toast({
          title: 'Resolution Check Complete',
          description: `${response.data.summary.resolved}/${response.data.summary.total} issues resolved`,
        });
      }
    } catch (err) {
      if (isStaleLoad(generation)) return;
      console.error('[ReviewPanel] Error checking resolution:', err);
      toast({
        title: 'Resolution Check Failed',
        description: err instanceof Error ? err.message : 'Failed to check resolution',
        variant: 'destructive',
      });
    } finally {
      if (!isStaleLoad(generation)) setResolutionCheckLoading(false);
    }
  };

  const getResolutionStatusBadge = (status: ResolutionResult['status']) => {
    switch (status) {
      case 'RESOLVED':
        return <Badge className="bg-green-100 text-green-800 border-green-300">Resolved</Badge>;
      case 'PARTIALLY_RESOLVED':
        return <Badge className="bg-yellow-100 text-yellow-800 border-yellow-300">Partial</Badge>;
      case 'NOT_RESOLVED':
        return <Badge variant="destructive">Not Resolved</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const handleApprove = async () => {
    // ⛔⛔ CAPTURED FOR BOTH PATHS. An earlier version of this comment claimed this handler writes
    // no item-scoped state on success "because onActionComplete()/onClose() are correct regardless
    // of which item is displayed". THE onClose() HALF OF THAT WAS WRONG, and codex-reviewer caught
    // it: closing the panel is an action on the item the operator is CURRENTLY looking at, not on
    // the one whose mutation just finished. A late approve of item A would close B's panel, toast
    // "Content Approved" over B, and — on reject — clear B's dialog state.
    // ⇒ onActionComplete() stays UNGUARDED: the mutation really happened and the parent's queue row
    // is stale no matter what is on screen. Everything after it is item-scoped.
    const generation = loadGenerationRef.current;
    if (!item) return;

    // CRITICAL: Require siteId for multi-tenancy security
    const siteId = item.siteId || selectedSite?.id;
    if (!siteId) {
      toast({
        title: 'Error',
        description: 'No site selected',
        variant: 'destructive',
      });
      return;
    }

    setActionLoading(true);

    try {
      await approveContent(item.contentType, item.id, siteId);
      onActionComplete();
      if (isStaleLoad(generation)) return;
      toast({
        title: 'Content Approved',
        description: 'The content has been approved and will be published.',
      });
      onClose();
    } catch (err) {
      if (isStaleLoad(generation)) return;
      console.error('[ReviewPanel] Error approving content:', err);
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to approve content',
        variant: 'destructive',
      });
    } finally {
      if (!isStaleLoad(generation)) setActionLoading(false);
    }
  };

  const handleReject = async () => {
    // ⛔⛔ CAPTURED FOR BOTH PATHS. An earlier version of this comment claimed this handler writes
    // no item-scoped state on success "because onActionComplete()/onClose() are correct regardless
    // of which item is displayed". THE onClose() HALF OF THAT WAS WRONG, and codex-reviewer caught
    // it: closing the panel is an action on the item the operator is CURRENTLY looking at, not on
    // the one whose mutation just finished. A late approve of item A would close B's panel, toast
    // "Content Approved" over B, and — on reject — clear B's dialog state.
    // ⇒ onActionComplete() stays UNGUARDED: the mutation really happened and the parent's queue row
    // is stale no matter what is on screen. Everything after it is item-scoped.
    const generation = loadGenerationRef.current;
    if (!item || !rejectionReason.trim()) return;

    // CRITICAL: Require siteId for multi-tenancy security
    const siteId = item.siteId || selectedSite?.id;
    if (!siteId) {
      toast({
        title: 'Error',
        description: 'No site selected',
        variant: 'destructive',
      });
      return;
    }

    setActionLoading(true);

    try {
      // GH #3214: only ever submit the data-quality flag for product_aeo. The checkbox is
      // hidden for other content types, but a stale `flagAsDataIssue` from a prior product
      // dialog must not be sent — the backend rejects a flagged non-product reject with 400,
      // which would block an otherwise-valid rejection with no visible recovery.
      const flagForSubmit = flagAsDataIssue && item.contentType === 'product_aeo';
      // task-edbnz2qj5c: a published product page (the panel's 'approved' IS 'published') submits
      // the reviewer's explicit choice; every other item keeps the checkbox semantics.
      const isPublishedProduct = item.contentType === 'product_aeo' && item.status === 'approved';
      const rejectKind: RejectKind = isPublishedProduct
        ? publishedRejectKind
        : (flagForSubmit ? 'data_quality' : 'plain');
      const rejectResult = await rejectContent(item.contentType, item.id, siteId, rejectionReason, rejectKind === 'data_quality', rejectKind);
      // Only claim the data-quality flag was recorded when the backend actually created a
      // data_quality_issues row (product_aeo). For other content types the flag is a no-op,
      // so we must not show a false "flagged" success (GH #3214).
      const flaggedRecorded = rejectKind === 'data_quality' && rejectResult?.data?.dataQualityIssueRecorded === true;
      const pulledBackForContent = rejectKind === 'content' && rejectResult?.data?.status === 'needs_review';
      onActionComplete();
      // ⛔ THE TOAST IS AN ITEM-SCOPED EFFECT AND BELONGS BELOW THE GUARD, exactly as in
      // handleApprove and handleResetToPending. I moved it in those two and left it here, because I
      // anchored the edit on the dialog-state clears and the toast sits above them — so the file
      // ended up asserting an invariant in a comment while one of its three siblings violated it.
      if (isStaleLoad(generation)) return;
      toast({
        title: 'Content Rejected',
        description: flaggedRecorded
          ? 'Content flagged as a data quality issue and sent back for review.'
          : pulledBackForContent
            ? 'Page sent back for regeneration as a content problem. No data issue was recorded against the product.'
            : 'The content has been rejected with feedback.',
      });
      setRejectDialogOpen(false);
      setFlagAsDataIssue(false);
      setPublishedRejectKind('content');
      setRejectionReason('');
      onClose();
    } catch (err) {
      if (isStaleLoad(generation)) return;
      console.error('[ReviewPanel] Error rejecting content:', err);
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to reject content',
        variant: 'destructive',
      });
    } finally {
      if (!isStaleLoad(generation)) setActionLoading(false);
    }
  };

  const handleResetToPending = async () => {
    // ⛔⛔ CAPTURED FOR BOTH PATHS. An earlier version of this comment claimed this handler writes
    // no item-scoped state on success "because onActionComplete()/onClose() are correct regardless
    // of which item is displayed". THE onClose() HALF OF THAT WAS WRONG, and codex-reviewer caught
    // it: closing the panel is an action on the item the operator is CURRENTLY looking at, not on
    // the one whose mutation just finished. A late approve of item A would close B's panel, toast
    // "Content Approved" over B, and — on reject — clear B's dialog state.
    // ⇒ onActionComplete() stays UNGUARDED: the mutation really happened and the parent's queue row
    // is stale no matter what is on screen. Everything after it is item-scoped.
    const generation = loadGenerationRef.current;
    if (!item) return;

    // CRITICAL: Require siteId for multi-tenancy security
    const siteId = item.siteId || selectedSite?.id;
    if (!siteId) {
      toast({
        title: 'Error',
        description: 'No site selected',
        variant: 'destructive',
      });
      return;
    }

    setActionLoading(true);

    try {
      await resetToPending(item.contentType, item.id, siteId);
      onActionComplete();
      if (isStaleLoad(generation)) return;
      toast({
        title: 'Reset to Pending',
        description: 'The content has been reset to pending review.',
      });
      onClose();
    } catch (err) {
      if (isStaleLoad(generation)) return;
      console.error('[ReviewPanel] Error resetting content to pending:', err);
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to reset content',
        variant: 'destructive',
      });
    } finally {
      if (!isStaleLoad(generation)) setActionLoading(false);
    }
  };

  const canEditRejection = item?.status === 'rejected' && content && user &&
    (content.approvedBy === user.id || user.role === 'super_admin');

  const handleEditRejectionReason = async () => {
    const generation = loadGenerationRef.current;
    if (!item || !content) return;

    const siteId = item.siteId || selectedSite?.id;
    if (!siteId) {
      toast({
        title: 'Error',
        description: 'No site selected',
        variant: 'destructive',
      });
      return;
    }

    if (!editedRejectionReason.trim()) {
      toast({
        title: 'Error',
        description: 'Rejection reason cannot be empty',
        variant: 'destructive',
      });
      return;
    }

    setEditRejectionLoading(true);

    try {
      const result = await updateRejectionReason(
        item.contentType,
        item.id,
        siteId,
        editedRejectionReason.trim()
      );
      if (isStaleLoad(generation)) return;
      setContent({ ...content, rejectionReason: result.data.rejectionReason });
      setEditingRejection(false);
      toast({
        title: 'Rejection Reason Updated',
        description: 'The rejection reason has been updated successfully.',
      });
    } catch (err) {
      if (isStaleLoad(generation)) return;
      console.error('[ReviewPanel] Error updating rejection reason:', err);
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to update rejection reason',
        variant: 'destructive',
      });
    } finally {
      if (!isStaleLoad(generation)) setEditRejectionLoading(false);
    }
  };

  const activeItemRef = useRef(item?.id);
  activeItemRef.current = item?.id;

  const handleRegenerated = async (completedItemId: string) => {
    // Queue invalidation belongs to the persistent observer; this callback updates only the open item.
    if (activeItemRef.current !== completedItemId) return;
    const generation = loadGenerationRef.current;
    await loadContentDetails(generation);
    if (isStaleLoad(generation)) return;
    setAnalysis(null);
    setKnowledgeRetrieval(null);
    setSavedEvaluation(null);
  };

  const handleVerifyDeployment = async () => {
    const generation = loadGenerationRef.current;
    if (!item) return;

    setVerifying(true);
    setVerificationResult(null);

    try {
      const response = await verifyDeployment(item.contentType, item.id);
      if (response.success && response.data) {
        if (isStaleLoad(generation)) return;
      setVerificationResult(response.data);

        const statusLabel = response.data.faqStatus ? getFaqStatusLabel(response.data.faqStatus) : 'Unknown';
        const similarity = response.data.faqSimilarity !== undefined
          ? `${Math.round(response.data.faqSimilarity * 100)}%`
          : 'N/A';

        toast({
          title: 'Deployment Verification Complete',
          description: `Status: ${statusLabel} (${similarity} similarity)`,
          variant: response.data.faqStatus === 'deployed' ? 'default' : 'destructive',
        });
      }
    } catch (err) {
      if (isStaleLoad(generation)) return;
      console.error('[ReviewPanel] Error verifying deployment:', err);
      toast({
        title: 'Verification Failed',
        description: err instanceof Error ? err.message : 'Failed to verify deployment',
        variant: 'destructive',
      });
    } finally {
      if (!isStaleLoad(generation)) setVerifying(false);
    }
  };

  const handleAnalyze = async () => {
    if (!item) return;
    if (!selectedSite?.id) {
      toast({
        title: 'Error',
        description: 'No site selected',
        variant: 'destructive',
      });
      return;
    }

    // ⛔ CAPTURED BEFORE THE FIRST await AND CHECKED AFTER EVERY ONE. An analysis for item A that
    // resolves after a switch to B would otherwise render A's score under B, and — on the grounded
    // path — install A's saved evaluation into B's panel, which is the wrong-evaluation action
    // hazard this whole PR exists to remove, arriving through the one door still left open.
    const generation = loadGenerationRef.current;

    setAnalysisLoading(true);
    setAnalysisError(null);
    setAnalysis(null);
    setKnowledgeRetrieval(null);
    // ⛔⛔ A NEW ANALYSIS INVALIDATES THE OLD EVALUATION'S ACTION BUTTONS — UNCONDITIONALLY.
    // The buttons render on `savedEvaluation &&` but pass an index into the CURRENT analysis's
    // ISSUES_FOUND, while handleTakeAction submits against `savedEvaluation.id`. Leave a stale
    // evaluation in place across a re-analysis and "resolve issue #2" records an action on the
    // PREVIOUS evaluation's item 2 — a different issue entirely, written into evaluation history
    // where nothing marks it as misfiled.
    // ⚠️ THE UNGROUNDED PATH IS WHAT MADE THIS REACHABLE: when we (correctly) stop persisting an
    // ungrounded analysis, nothing overwrites savedEvaluation any more, so the old one survives
    // NEXT TO a freshly rendered set of issues. Not persisting was right; not clearing was the bug
    // that came with it. Clearing here rather than only on the ungrounded branch, because the
    // hazard is "the evaluation on screen is not the analysis on screen" — which is true for a
    // moment on EVERY re-analysis, not just the ungrounded ones.
    setSavedEvaluation(null);
    setIssueActions([]);

    try {
      const response = await analyzeContent(item.contentType, item.id, selectedSite.id);
      if (isStaleLoad(generation)) return;
      if (response.success && response.analysis) {
        setAnalysis(response.analysis);
        setAnalysisExpanded(true);

        // ⛔⛔ `success` MEANS THE ANALYSIS RAN, NEVER THAT IT HAD ITS KNOWLEDGE. The backend returns
        // success:true after a total retrieval failure by design, so this panel used to show a score,
        // a recommendation and the word "Complete" for a verdict grounded in nothing at all.
        // ⚠️ An ABSENT signal is "we do not know", which must not render as "it worked" — the
        // undefined case is therefore grouped with the failures, not with the successes.
        const grounded = response.knowledgeRetrieval?.ok === true;
        setKnowledgeRetrieval(response.knowledgeRetrieval ?? { ok: false, code: 'KNOWLEDGE_RETRIEVAL_UNREPORTED' });

        // ⛔ EXPLICIT POLICY: AN UNGROUNDED VERDICT IS NOT PERSISTED. Saving it writes a score and a
        // recommendation into the evaluation record, where nothing carries the caveat — the durable
        // version of the same lie, and the one a later reader has no way to detect. The analysis is
        // still SHOWN, clearly marked, because it is the operator's call what to do with it.
        if (selectedSite?.id && grounded) {
          try {
            const saveResponse = await saveEvaluation(
              item.contentType,
              item.id,
              selectedSite.id,
              response.analysis,
              'gpt-4o'
            );
            // ⚠️ CHECKED AGAIN AFTER THE SAVE, not only after the analysis. The save is a second
            // suspension point and it is the more dangerous one: it installs an evaluation ID that
            // handleTakeAction later submits against.
            if (isStaleLoad(generation)) return;
            if (saveResponse.success && saveResponse.data) {
              setSavedEvaluation(saveResponse.data);
              setIssueActions([]);
            }
          } catch (saveErr) {
            console.error('[ReviewPanel] Error saving evaluation:', saveErr);
          }
        }

        // ⛔ THE TITLE IS WHAT IS READ. A destructive "Analysis Incomplete" above the same numbers is
        // the difference between an operator who checks and one who approves.
        toast(grounded ? {
          title: 'Analysis Complete',
          description: `Quality Score: ${response.analysis.QUALITY_SCORE}/10 - ${response.analysis.RECOMMENDATION}`,
        } : {
          title: 'Analysis Incomplete — knowledge did not load',
          description: `This verdict was NOT grounded in domain knowledge and has not been saved. Treat the ${response.analysis.QUALITY_SCORE}/10 and "${response.analysis.RECOMMENDATION}" as unverified.`,
          variant: 'destructive',
        });
      }
    } catch (err) {
      // ⛔ AN ERROR IS ALSO ITEM-SCOPED. "Analysis Failed" for item A, shown while B is on screen,
      // reports a failure about content the operator is not looking at.
      if (isStaleLoad(generation)) return;
      console.error('[ReviewPanel] Error analyzing content:', err);
      setAnalysisError(err instanceof Error ? err.message : 'Failed to analyze content');
      toast({
        title: 'Analysis Failed',
        description: err instanceof Error ? err.message : 'Failed to analyze content',
        variant: 'destructive',
      });
    } finally {
      // ⚠️ The spinner belongs to the LATEST request. A superseded run clearing it would report the
      // current analysis as finished while it is still running.
      if (!isStaleLoad(generation)) setAnalysisLoading(false);
    }
  };

  // Story 48: Open knowledge creation modal for domain_knowledge solutions
  const handleOpenKnowledgeModal = (issue: ContentIssue, issueIndex: number) => {
    setKnowledgeModalIssue(issue);
    setKnowledgeModalIssueIndex(issueIndex);
    setKnowledgeModalOpen(true);
  };

  // Story 48: Handle knowledge created from issue
  const handleKnowledgeCreated = (knowledgeId: string) => {
    // Mark the issue as having had action taken
    if (savedEvaluation && knowledgeModalIssue) {
      setIssueActions(prev => [...prev, {
        id: knowledgeId,
        evaluationId: savedEvaluation.id,
        itemType: 'issue',
        itemIndex: knowledgeModalIssueIndex,
        actionType: 'CREATE_KNOWLEDGE',
        actionStatus: 'completed',
        referenceType: 'domain_knowledge',
        referenceId: knowledgeId,
        createdAt: new Date().toISOString(),
      }]);
    }
    setKnowledgeModalOpen(false);
    setKnowledgeModalIssue(null);
  };

  const handleTakeAction = async (
    itemType: 'issue' | 'gap',
    itemIndex: number,
    issue: ContentIssue
  ) => {
    if (!savedEvaluation) {
      toast({
        title: 'Error',
        description: 'No saved evaluation found. Please re-analyze first.',
        variant: 'destructive',
      });
      return;
    }

    // ⛔ THE HIGHEST-CONSEQUENCE GUARD IN THIS FILE. This submits against savedEvaluation.id with an
    // index into the DISPLAYED analysis; if the item changed while the request was in flight, the
    // response appends an action describing a different item's issue into the on-screen list.
    const generation = loadGenerationRef.current;

    const actionKey = `${itemType}-${itemIndex}`;
    setActionInProgress(actionKey);

    const suggestedAction = getSuggestedActionForIssue(issue.type);

    try {
      const response = await takeIssueAction(
        savedEvaluation.id,
        itemType,
        itemIndex,
        suggestedAction.action,
        issue
      );

      if (response.success) {
        // Update local actions state with itemType and itemIndex for matching
        if (isStaleLoad(generation)) return;
        setIssueActions(prev => [...prev, {
          ...response.action,
          itemType,
          itemIndex,
        }]);

        // Get follow-up location based on action type
        const followUpInfo = getFollowUpLocation(suggestedAction.action);

        toast({
          title: 'Action Requested',
          description: (
            <div className="space-y-1">
              <p>{suggestedAction.description}</p>
              {followUpInfo && (
                <a
                  href={followUpInfo.path}
                  className="text-primary underline hover:no-underline text-xs"
                  onClick={(e) => {
                    e.preventDefault();
                    window.location.href = followUpInfo.path;
                  }}
                >
                  {followUpInfo.label} →
                </a>
              )}
            </div>
          ),
        });
      }
    } catch (err) {
      if (isStaleLoad(generation)) return;
      console.error('[ReviewPanel] Error taking action:', err);
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to take action',
        variant: 'destructive',
      });
    } finally {
      if (!isStaleLoad(generation)) setActionInProgress(null);
    }
  };

  const handleCreateProposals = async () => {
    const generation = loadGenerationRef.current;
    if (!item || !analysis) return;

    setCreatingProposals(true);

    try {
      const response = await createKnowledgeProposals(
        item.contentType,
        item.id,
        analysis
      );
      if (response.success) {
        const count = response.knowledgeProposals.length;
        // Store created proposals for display
        if (isStaleLoad(generation)) return;
        setCreatedProposals(response.knowledgeProposals.map(p => ({
          id: p.id,
          title: p.title
        })));
        toast({
          title: 'Knowledge Proposals Created',
          description: (
            <div className="space-y-1">
              <p>Created {count} knowledge proposal{count !== 1 ? 's' : ''} for review.</p>
              <a
                href="/domain-knowledge"
                className="text-primary underline hover:no-underline"
                onClick={(e) => {
                  e.preventDefault();
                  window.location.href = '/domain-knowledge';
                }}
              >
                View in Domain Knowledge Dashboard
              </a>
            </div>
          ),
        });
      }
    } catch (err) {
      if (isStaleLoad(generation)) return;
      console.error('[ReviewPanel] Error creating knowledge proposals:', err);
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to create proposals',
        variant: 'destructive',
      });
    } finally {
      if (!isStaleLoad(generation)) setCreatingProposals(false);
    }
  };

  const getQualityScoreColor = (score: number): string => {
    if (score >= 8) return 'text-green-600';
    if (score >= 6) return 'text-yellow-600';
    if (score >= 4) return 'text-orange-600';
    return 'text-red-600';
  };

  const getRecommendationBadge = (recommendation: string) => {
    switch (recommendation) {
      case 'APPROVE':
        return <Badge className="bg-green-100 text-green-800 border-green-300">Recommend Approve</Badge>;
      case 'APPROVE_WITH_NOTES':
        return <Badge className="bg-yellow-100 text-yellow-800 border-yellow-300">Approve with Notes</Badge>;
      case 'REJECT':
        return <Badge className="bg-red-100 text-red-800 border-red-300">Recommend Reject</Badge>;
      default:
        return <Badge variant="secondary">{recommendation}</Badge>;
    }
  };

  const getSeverityBadge = (severity: string) => {
    switch (severity) {
      case 'HIGH':
        return <Badge variant="destructive" className="text-xs">High</Badge>;
      case 'MEDIUM':
        return <Badge className="bg-yellow-100 text-yellow-800 text-xs">Medium</Badge>;
      case 'LOW':
        return <Badge variant="secondary" className="text-xs">Low</Badge>;
      default:
        return <Badge variant="secondary" className="text-xs">{severity}</Badge>;
    }
  };

  // Story 48: Helper for displaying recommended solution type info
  const getSolutionTypeInfo = (solution: RecommendedSolution) => {
    switch (solution.type) {
      case 'domain_knowledge':
        return {
          icon: <BookOpen className="h-4 w-4 text-purple-500" />,
          label: 'Create Domain Knowledge',
          colorClass: 'bg-purple-50 dark:bg-purple-950/20 border-purple-200 dark:border-purple-800',
          buttonVariant: 'default' as const,
          buttonClass: 'bg-purple-600 hover:bg-purple-700',
        };
      case 'data_fix':
        return {
          icon: <Database className="h-4 w-4 text-orange-500" />,
          label: 'Create Data Issue',
          colorClass: 'bg-orange-50 dark:bg-orange-950/20 border-orange-200 dark:border-orange-800',
          buttonVariant: 'default' as const,
          buttonClass: 'bg-orange-600 hover:bg-orange-700',
        };
      case 'code_bug':
        return {
          icon: <Bug className="h-4 w-4 text-red-500" />,
          label: 'Flag Bug',
          colorClass: 'bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800',
          buttonVariant: 'destructive' as const,
          buttonClass: '',
        };
      default:
        return {
          icon: <Lightbulb className="h-4 w-4 text-blue-500" />,
          label: 'Send Suggestion',
          colorClass: 'bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800',
          buttonVariant: 'default' as const,
          buttonClass: '',
        };
    }
  };

  const renderContent = () => {
    if (!content) return null;

    switch (content.contentType) {
      case 'ai_article':
        return (
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold">{content.title}</h3>
              {content.metaDescription && (
                <p className="text-sm text-muted-foreground mt-1">
                  {content.metaDescription as string}
                </p>
              )}
            </div>
            <Separator />
            <div className="prose prose-sm max-w-none dark:prose-invert">
              {content.contentHtml ? (
                <div
                  dangerouslySetInnerHTML={{ __html: cleanHtmlContent(content.contentHtml as string) }}
                />
              ) : content.content ? (
                <div
                  dangerouslySetInnerHTML={{ __html: cleanHtmlContent(content.content as string) }}
                />
              ) : (
                <p className="text-muted-foreground">No content available</p>
              )}
            </div>
            {content.wordCount && (
              <div className="text-xs text-muted-foreground">
                Word count: {(content.wordCount as number).toLocaleString()}
              </div>
            )}
          </div>
        );

      case 'product_aeo':
        return (
          <ProductAEOViewer
            title={content.title}
            content={content.content || {}}
            metadata={content.metadata as Record<string, unknown>}
            generatedAt={(content.content as Record<string, unknown>)?.generated_at as string || content.updatedAt as string || content.createdAt as string}
            generationMetadata={(content.content as Record<string, unknown>)?.generation_metadata as { generation_model?: string; generation_cost_usd?: number; input_tokens?: number; output_tokens?: number; call_count?: number; }}
            isSuperAdmin={user?.role === 'super_admin'}
            showCopyButtons={true}
            currentLanguage={content.currentLanguage}
            sourceLanguage={content.sourceLanguage}
            availableLanguages={content.availableLanguages}
            expectedSectionKeys={content.expectedSectionKeys}
            onLanguageChange={handleLanguageChange}
          />
        );

      case 'category_aeo':
        return (
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold">{content.title}</h3>
              <div className="flex gap-2 mt-2">
                {content.language && (
                  <Badge variant="outline">{content.language as string}</Badge>
                )}
                {content.market && (
                  <Badge variant="outline">{content.market as string}</Badge>
                )}
              </div>
            </div>
            <Separator />
            {content.content && typeof content.content === 'object' && (
              <CategoryAEOViewer
                sections={content.content as Record<string, unknown>}
                showCopyButtons={true}
              />
            )}
          </div>
        );

      default:
        return (
          <div className="p-4 text-muted-foreground">
            Content preview not available
          </div>
        );
    }
  };

  const renderAEOSection = (title: string, data: unknown) => {
    if (!data) return null;

    // Handle FAQs specifically
    if (title === 'FAQs' && Array.isArray(data)) {
      return (
        <div className="space-y-2">
          <h4 className="font-medium text-sm">{title}</h4>
          <div className="space-y-3">
            {data.map((faq: { question?: string; answer?: string }, idx: number) => (
              <div key={idx} className="bg-muted p-3 rounded-md">
                <p className="font-medium text-sm text-primary">{faq.question}</p>
                <p className="text-sm mt-1 text-muted-foreground">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      );
    }

    // Handle arrays of objects (Top Picks, Best Sellers, etc.)
    if (Array.isArray(data)) {
      return (
        <div className="space-y-2">
          <h4 className="font-medium text-sm">{title}</h4>
          <div className="space-y-2">
            {data.map((item: Record<string, unknown>, idx: number) => (
              <div key={idx} className="bg-muted p-3 rounded-md text-sm">
                {typeof item === 'object' && item !== null ? (
                  <div className="space-y-1">
                    {Object.entries(item).map(([key, value]) => (
                      <div key={key}>
                        <span className="font-medium capitalize">{key.replace(/_/g, ' ')}: </span>
                        <span className="text-muted-foreground">
                          {typeof value === 'string' ? value : JSON.stringify(value)}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p>{String(item)}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      );
    }

    // Handle strings
    if (typeof data === 'string') {
      return (
        <div className="space-y-2">
          <h4 className="font-medium text-sm">{title}</h4>
          <div className="bg-muted p-3 rounded-md text-sm">
            <p>{data}</p>
          </div>
        </div>
      );
    }

    // Handle objects (Buyer Guide, etc.)
    if (typeof data === 'object' && data !== null) {
      return (
        <div className="space-y-2">
          <h4 className="font-medium text-sm">{title}</h4>
          <div className="bg-muted p-3 rounded-md text-sm space-y-2">
            {Object.entries(data as Record<string, unknown>).map(([key, value]) => (
              <div key={key}>
                <span className="font-medium capitalize">{key.replace(/_/g, ' ')}: </span>
                {Array.isArray(value) ? (
                  <ul className="list-disc list-inside ml-2 text-muted-foreground">
                    {value.map((v, i) => <li key={i}>{typeof v === 'string' ? v : JSON.stringify(v)}</li>)}
                  </ul>
                ) : (
                  <span className="text-muted-foreground">
                    {typeof value === 'string' ? value : JSON.stringify(value)}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onClose}>
        <SheetContent className="w-[900px] sm:max-w-[900px] overflow-hidden flex flex-col">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              {item && getContentTypeIcon(item.contentType)}
              <span className="truncate">{item?.title || 'Review Content'}</span>
            </SheetTitle>
            <SheetDescription className="flex items-center gap-4">
              {item && (
                <>
                  <Badge variant="outline">
                    {getContentTypeLabel(item.contentType)}
                  </Badge>
                  <span className="flex items-center gap-1 text-xs">
                    <Globe className="h-3 w-3" />
                    {item.siteName}
                  </span>
                  <span className="flex items-center gap-1 text-xs">
                    <Calendar className="h-3 w-3" />
                    {format(new Date(item.createdAt), 'MMM d, yyyy')}
                  </span>
                </>
              )}
            </SheetDescription>
          </SheetHeader>

          <ScrollArea className="flex-1 mt-4 -mx-6 px-6">
            {loading ? (
              <div className="space-y-4">
                <Skeleton className="h-8 w-3/4" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-2/3" />
                <Separator className="my-4" />
                <Skeleton className="h-32 w-full" />
              </div>
            ) : error ? (
              <div className="text-center py-8">
                <p className="text-destructive">{error}</p>
                <Button
                  variant="ghost"
                  onClick={() => loadContentDetails(loadGenerationRef.current)}
                  className="mt-2"
                >
                  Try Again
                </Button>
              </div>
            ) : (
              <div className="space-y-6">
                {renderContent()}

                {/* Quick Actions - for product_aeo content */}
                {content?.contentType === 'product_aeo' && (
                  <div className="space-y-3 pt-2 border-t">
                    <div className="flex gap-2 flex-wrap">
                      {/* Link to original product page */}
                      {content.metadata?.product_url && (
                        <Button variant="outline" size="sm" asChild>
                          <a
                            href={content.metadata.product_url as string}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <Globe className="h-4 w-4 mr-2" />
                            View Original Page
                            <ExternalLink className="h-3 w-3 ml-1" />
                          </a>
                        </Button>
                      )}
                      {/* Link to Enrichment Review - Research tab */}
                      {/* Use sourceId (knowledge_base_entries UUID) not metadata.product_id (SKU) */}
                      {(content as { sourceId?: string }).sourceId && (
                        <Button variant="outline" size="sm" asChild>
                          <a
                            href={`/research/enrichment-review?mode=research&productId=${(content as { sourceId?: string }).sourceId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <Database className="h-4 w-4 mr-2" />
                            View Source Data
                            <ExternalLink className="h-3 w-3 ml-1" />
                          </a>
                        </Button>
                      )}
                    </div>
                    {(content as { sourceId?: string }).sourceId && (
                      <AttributeSourceSummary productId={(content as { sourceId?: string }).sourceId as string} />
                    )}
                  </div>
                )}

                {/* Previous Evaluation Banner - Story 46 */}
                {previousEvaluation && previousEvaluation.issuesFound.length > 0 && !showResolutionPanel && (
                  <div className="p-4 border border-blue-200 rounded-lg bg-blue-50 dark:bg-blue-950/20 dark:border-blue-800">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <History className="h-5 w-5 text-blue-500" />
                        <div>
                          <p className="font-medium text-blue-800 dark:text-blue-200">
                            Previous Evaluation Found
                          </p>
                          <p className="text-sm text-blue-600 dark:text-blue-400">
                            Score: {previousEvaluation.qualityScore}/10 with {previousEvaluation.issuesFound.length} issue{previousEvaluation.issuesFound.length !== 1 ? 's' : ''} • {format(new Date(previousEvaluation.evaluatedAt), 'MMM d, yyyy')}
                          </p>
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleCheckResolution}
                        disabled={resolutionCheckLoading}
                        className="border-blue-300 text-blue-700 hover:bg-blue-100 dark:border-blue-700 dark:text-blue-300"
                      >
                        {resolutionCheckLoading ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <GitCompare className="h-4 w-4 mr-2" />
                        )}
                        Check Resolution
                      </Button>
                    </div>
                  </div>
                )}

                {/* Resolution Results Panel - Story 46 */}
                {showResolutionPanel && resolutionResults && (
                  <div className="border rounded-lg overflow-hidden">
                    <div className="p-4 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-950/30 dark:to-purple-950/30 border-b">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <TrendingUp className="h-5 w-5 text-purple-500" />
                          <h3 className="font-semibold">Issue Resolution Analysis</h3>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowResolutionPanel(false)}
                        >
                          <MinusCircle className="h-4 w-4" />
                        </Button>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">
                        Comparing current content against {previousEvaluation?.issuesFound.length} previous issues
                      </p>
                    </div>

                    {/* Summary Stats */}
                    <div className="p-4 bg-muted/30 grid grid-cols-4 gap-4 text-center border-b">
                      <div>
                        <div className="text-2xl font-bold text-muted-foreground">{resolutionResults.summary.total}</div>
                        <div className="text-xs text-muted-foreground">Total Issues</div>
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-green-600">{resolutionResults.summary.resolved}</div>
                        <div className="text-xs text-muted-foreground">Resolved</div>
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-yellow-600">{resolutionResults.summary.partiallyResolved}</div>
                        <div className="text-xs text-muted-foreground">Partial</div>
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-red-600">{resolutionResults.summary.notResolved}</div>
                        <div className="text-xs text-muted-foreground">Not Resolved</div>
                      </div>
                    </div>

                    {/* Overall Improvement */}
                    <div className="p-4 border-b">
                      <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                        <Sparkles className="h-4 w-4 text-purple-500" />
                        Overall Improvement
                      </h4>
                      <p className="text-sm text-muted-foreground">{resolutionResults.overallImprovement}</p>
                    </div>

                    {/* Individual Resolution Results */}
                    <div className="p-4 space-y-3">
                      <h4 className="font-medium text-sm">Issue-by-Issue Analysis</h4>
                      {resolutionResults.resolutions.map((resolution, idx) => (
                        <div
                          key={idx}
                          className={`p-3 rounded-lg border ${
                            resolution.status === 'RESOLVED' ? 'bg-green-50 border-green-200 dark:bg-green-950/20 dark:border-green-800' :
                            resolution.status === 'PARTIALLY_RESOLVED' ? 'bg-yellow-50 border-yellow-200 dark:bg-yellow-950/20 dark:border-yellow-800' :
                            'bg-red-50 border-red-200 dark:bg-red-950/20 dark:border-red-800'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium">Issue #{resolution.issueIndex + 1}</span>
                            {getResolutionStatusBadge(resolution.status)}
                          </div>
                          <p className="text-sm text-muted-foreground mb-1">
                            <strong>Original:</strong> {resolution.originalIssue}
                          </p>
                          <p className="text-sm">
                            <strong>Analysis:</strong> {resolution.explanation}
                          </p>
                          {resolution.evidence && (
                            <p className="text-xs text-muted-foreground mt-1 italic">
                              "{resolution.evidence}"
                            </p>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Remaining Concerns */}
                    {resolutionResults.remainingConcerns.length > 0 && (
                      <div className="p-4 border-t bg-amber-50 dark:bg-amber-950/20">
                        <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                          <AlertTriangle className="h-4 w-4 text-amber-500" />
                          Remaining Concerns
                        </h4>
                        <ul className="space-y-1">
                          {resolutionResults.remainingConcerns.map((concern, idx) => (
                            <li key={idx} className="text-sm text-muted-foreground flex items-start gap-2">
                              <span className="text-amber-500">&bull;</span>
                              {concern}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                {/* Quality Evolution Timeline - Story 46 */}
                {evaluationHistory.length > 1 && (
                  <Collapsible open={showQualityEvolution} onOpenChange={setShowQualityEvolution}>
                    <div className="border rounded-lg">
                      <CollapsibleTrigger className="w-full">
                        <div className="p-4 flex items-center justify-between hover:bg-muted/50 transition-colors">
                          <div className="flex items-center gap-3">
                            <History className="h-5 w-5 text-indigo-500" />
                            <div className="text-left">
                              <h3 className="font-semibold">Quality Evolution</h3>
                              <p className="text-sm text-muted-foreground">
                                {evaluationHistory.length} evaluation{evaluationHistory.length !== 1 ? 's' : ''} over time
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            {evaluationStats && (
                              <div className="flex items-center gap-2">
                                {evaluationStats.scoreImprovement > 0 ? (
                                  <Badge className="bg-green-100 text-green-800 border-green-300">
                                    +{evaluationStats.scoreImprovement.toFixed(1)} improvement
                                  </Badge>
                                ) : evaluationStats.scoreImprovement < 0 ? (
                                  <Badge className="bg-red-100 text-red-800 border-red-300">
                                    {evaluationStats.scoreImprovement.toFixed(1)} decline
                                  </Badge>
                                ) : (
                                  <Badge variant="secondary">No change</Badge>
                                )}
                              </div>
                            )}
                            {showQualityEvolution ? (
                              <ChevronUp className="h-4 w-4 text-muted-foreground" />
                            ) : (
                              <ChevronDown className="h-4 w-4 text-muted-foreground" />
                            )}
                          </div>
                        </div>
                      </CollapsibleTrigger>

                      <CollapsibleContent>
                        <div className="border-t p-4">
                          {/* Stats Summary */}
                          {evaluationStats && (
                            <div className="grid grid-cols-4 gap-4 mb-4 p-3 bg-muted/30 rounded-lg">
                              <div className="text-center">
                                <div className="text-2xl font-bold text-indigo-600">{evaluationStats.totalEvaluations}</div>
                                <div className="text-xs text-muted-foreground">Evaluations</div>
                              </div>
                              <div className="text-center">
                                <div className="text-2xl font-bold">{evaluationStats.averageScore.toFixed(1)}</div>
                                <div className="text-xs text-muted-foreground">Avg Score</div>
                              </div>
                              <div className="text-center">
                                <div className="text-2xl font-bold text-muted-foreground">{evaluationStats.firstScore}/10</div>
                                <div className="text-xs text-muted-foreground">First Score</div>
                              </div>
                              <div className="text-center">
                                <div className={`text-2xl font-bold ${evaluationStats.latestScore >= 8 ? 'text-green-600' : evaluationStats.latestScore >= 6 ? 'text-yellow-600' : 'text-red-600'}`}>
                                  {evaluationStats.latestScore}/10
                                </div>
                                <div className="text-xs text-muted-foreground">Latest Score</div>
                              </div>
                            </div>
                          )}

                          {/* Timeline */}
                          <div className="space-y-3">
                            {evaluationHistory.map((eval_, idx) => {
                              const isLatest = idx === 0;
                              const prevEval = evaluationHistory[idx + 1];
                              const scoreDiff = prevEval ? eval_.qualityScore - prevEval.qualityScore : 0;

                              return (
                                <div
                                  key={eval_.id}
                                  className={`flex items-center gap-3 p-3 rounded-lg border ${
                                    isLatest ? 'bg-indigo-50 border-indigo-200 dark:bg-indigo-950/20 dark:border-indigo-800' : 'bg-muted/30'
                                  }`}
                                >
                                  {/* Score Circle */}
                                  <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg ${
                                    eval_.qualityScore >= 8 ? 'bg-green-100 text-green-700' :
                                    eval_.qualityScore >= 6 ? 'bg-yellow-100 text-yellow-700' :
                                    'bg-red-100 text-red-700'
                                  }`}>
                                    {eval_.qualityScore}
                                  </div>

                                  {/* Details */}
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                      {isLatest && (
                                        <Badge variant="outline" className="text-xs">Latest</Badge>
                                      )}
                                      <Badge variant="secondary" className="text-xs">
                                        {eval_.recommendation}
                                      </Badge>
                                      {scoreDiff !== 0 && (
                                        <span className={`text-xs font-medium ${
                                          scoreDiff > 0 ? 'text-green-600' : 'text-red-600'
                                        }`}>
                                          {scoreDiff > 0 ? '+' : ''}{scoreDiff}
                                        </span>
                                      )}
                                    </div>
                                    <div className="text-xs text-muted-foreground mt-1">
                                      {format(new Date(eval_.evaluatedAt), 'MMM d, yyyy h:mm a')}
                                      {eval_.evaluatorName && ` • by ${eval_.evaluatorName}`}
                                    </div>
                                    {eval_.issuesFound.length > 0 && (
                                      <div className="text-xs text-muted-foreground mt-1">
                                        {eval_.issuesFound.length} issue{eval_.issuesFound.length !== 1 ? 's' : ''} found
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </CollapsibleContent>
                    </div>
                  </Collapsible>
                )}

                {/* AI Analysis Section */}
                {content && (
                  <div className="border-t pt-4 mt-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold flex items-center gap-2">
                        <Sparkles className="h-5 w-5 text-purple-500" />
                        AI Analysis
                      </h3>
                      {!analysis && !analysisLoading && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleAnalyze}
                          disabled={analysisLoading}
                        >
                          <Sparkles className="h-4 w-4 mr-2" />
                          Analyze with AI
                        </Button>
                      )}
                    </div>

                    {analysisLoading && (
                      <div className="p-6 border rounded-lg bg-muted/50">
                        <div className="flex items-center gap-3">
                          <Loader2 className="h-5 w-5 animate-spin text-purple-500" />
                          <div>
                            <p className="font-medium">Analyzing content...</p>
                            <p className="text-sm text-muted-foreground">
                              The AI is evaluating quality, identifying issues, and suggesting improvements.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {analysisError && (
                      <div className="p-4 border border-destructive rounded-lg bg-destructive/10">
                        <div className="flex items-center gap-2">
                          <AlertTriangle className="h-5 w-5 text-destructive" />
                          <p className="text-destructive">{analysisError}</p>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleAnalyze}
                          className="mt-2"
                        >
                          Retry Analysis
                        </Button>
                      </div>
                    )}

                    {/*
                      ⛔⛔ A VERDICT THAT DID NOT HAVE ITS KNOWLEDGE SAYS SO, NEXT TO THE SCORE.
                      A toast is dismissed in three seconds and is gone from a panel the operator may
                      sit in for minutes; the score and the recommendation stay on screen the whole
                      time. The caveat has to live where the number lives.

                      ⚠️ RENDERED ONLY WHEN A SIGNAL EXISTS AND IS NOT OK — `null` means no analysis
                      was run in this session (e.g. one loaded from saved history, whose grounding we
                      genuinely do not know). Warning on `null` would put a red banner on every
                      historical evaluation, which is the over-correction: a warning shown always is
                      a warning read never.
                    */}
                    {knowledgeRetrieval && !knowledgeRetrieval.ok && (
                      <div className="border border-destructive/50 bg-destructive/10 rounded-lg p-3 mb-3">
                        <div className="flex items-start gap-2">
                          <AlertTriangle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
                          <div className="text-sm">
                            <p className="font-semibold text-destructive">
                              This verdict was not grounded in domain knowledge
                            </p>
                            <p className="text-muted-foreground mt-1">
                              {knowledgeRetrieval.code === 'KNOWLEDGE_SOURCES_PARTIAL_FAILURE'
                                ? `Only some knowledge sources loaded (${knowledgeRetrieval.failedSources?.join(', ')} failed of ${knowledgeRetrieval.attemptedSources} attempted). The score and recommendation below were produced from an incomplete rule set.`
                                : knowledgeRetrieval.code === 'KNOWLEDGE_SOURCES_ALL_FAILED'
                                ? `No knowledge source loaded (${knowledgeRetrieval.failedSources?.join(', ')}). The score and recommendation below were produced with no domain rules at all.`
                                : knowledgeRetrieval.code === 'KNOWLEDGE_RETRIEVAL_UNREPORTED'
                                ? 'The backend did not report whether its knowledge loaded, so grounding is unknown — not confirmed.'
                                : 'Knowledge retrieval failed, so the judge ran with no domain rules.'}
                            </p>
                            <p className="text-muted-foreground mt-1">
                              This analysis has <span className="font-medium">not been saved</span>. Re-run it once retrieval is healthy before acting on the recommendation.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {analysis && (
                      <Collapsible open={analysisExpanded} onOpenChange={setAnalysisExpanded}>
                        <div className="border rounded-lg">
                          {/* Analysis Header - always visible */}
                          <CollapsibleTrigger className="w-full">
                            <div className="p-4 flex items-center justify-between hover:bg-muted/50 transition-colors">
                              <div className="flex items-center gap-4">
                                {/* Quality Score */}
                                <div className="text-center">
                                  <div className={`text-3xl font-bold ${getQualityScoreColor(analysis.QUALITY_SCORE)}`}>
                                    {analysis.QUALITY_SCORE}
                                  </div>
                                  <div className="text-xs text-muted-foreground">/ 10</div>
                                </div>
                                <Separator orientation="vertical" className="h-12" />
                                <div className="text-left">
                                  {getRecommendationBadge(analysis.RECOMMENDATION)}
                                  <p className="text-sm text-muted-foreground mt-1">
                                    {analysis.SUMMARY}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <Badge variant="outline">
                                  {analysis.ISSUES_FOUND.length} issue{analysis.ISSUES_FOUND.length !== 1 ? 's' : ''}
                                </Badge>
                                <Badge variant="outline">
                                  {analysis.KNOWLEDGE_GAPS.length} gap{analysis.KNOWLEDGE_GAPS.length !== 1 ? 's' : ''}
                                </Badge>
                                {analysisExpanded ? (
                                  <ChevronUp className="h-4 w-4 text-muted-foreground" />
                                ) : (
                                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                )}
                              </div>
                            </div>
                          </CollapsibleTrigger>

                          <CollapsibleContent>
                            <div className="border-t p-4 space-y-6">
                              {/* Strengths */}
                              {analysis.STRENGTHS.length > 0 && (
                                <div>
                                  <h4 className="font-medium flex items-center gap-2 mb-2">
                                    <ThumbsUp className="h-4 w-4 text-green-500" />
                                    Strengths
                                  </h4>
                                  <ul className="space-y-1">
                                    {analysis.STRENGTHS.map((strength, idx) => (
                                      <li key={idx} className="text-sm text-muted-foreground flex items-start gap-2">
                                        <span className="text-green-500">+</span>
                                        {strength}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}

                              {/* Issues Found */}
                              {analysis.ISSUES_FOUND.length > 0 && (
                                <div>
                                  <h4 className="font-medium flex items-center gap-2 mb-2">
                                    <AlertTriangle className="h-4 w-4 text-yellow-500" />
                                    Issues Found
                                  </h4>
                                  <div className="space-y-4">
                                    {analysis.ISSUES_FOUND.map((issue, idx) => {
                                      const existingAction = hasActionBeenTaken(issueActions, 'issue', idx);
                                      const suggestedAction = getSuggestedActionForIssue(issue.type);
                                      const isActionInProgress = actionInProgress === `issue-${idx}`;
                                      const solutionInfo = issue.recommended_solution
                                        ? getSolutionTypeInfo(issue.recommended_solution)
                                        : null;

                                      return (
                                        <div key={idx} className="border rounded-lg overflow-hidden">
                                          {/* Issue Header */}
                                          <div className="p-3 bg-muted">
                                            <div className="flex items-center justify-between mb-1">
                                              <div className="flex items-center gap-2">
                                                {getSeverityBadge(issue.severity)}
                                                <Badge variant="outline" className="text-xs">
                                                  {getIssueTypeLabel(issue.type)}
                                                </Badge>
                                              </div>
                                              {existingAction && (
                                                <div className="flex items-center gap-1 text-green-600">
                                                  <CheckCircle className="h-4 w-4" />
                                                  <span className="text-xs font-medium">Action Taken</span>
                                                </div>
                                              )}
                                            </div>
                                            <p className="text-sm font-medium">{issue.description}</p>
                                            {issue.location && (
                                              <p className="text-xs text-muted-foreground mt-1 italic">
                                                "{issue.location}"
                                              </p>
                                            )}
                                            {issue.suggestion && (
                                              <p className="text-sm text-blue-600 mt-2">
                                                <Lightbulb className="h-3 w-3 inline mr-1" />
                                                {issue.suggestion}
                                              </p>
                                            )}
                                          </div>

                                          {/* Story 48: Recommended Solution Card */}
                                          {issue.recommended_solution && solutionInfo && !existingAction && (
                                            <div className={`p-3 border-t ${solutionInfo.colorClass}`}>
                                              <div className="flex items-center gap-2 mb-2">
                                                <Target className="h-4 w-4 text-muted-foreground" />
                                                <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                                                  Recommended Solution
                                                </span>
                                              </div>

                                              <div className="flex items-start gap-3">
                                                <div className="flex-1">
                                                  <div className="flex items-center gap-2 mb-1">
                                                    {solutionInfo.icon}
                                                    <span className="text-sm font-medium">
                                                      {issue.recommended_solution.type === 'domain_knowledge'
                                                        ? 'Create Domain Knowledge'
                                                        : issue.recommended_solution.type === 'data_fix'
                                                        ? 'Create Data Issue'
                                                        : 'Flag for Investigation'}
                                                    </span>
                                                  </div>
                                                  <p className="text-xs text-muted-foreground mb-2">
                                                    {issue.recommended_solution.reason}
                                                  </p>

                                                  {/* Scope and Impact for domain_knowledge */}
                                                  {issue.recommended_solution.type === 'domain_knowledge' && (
                                                    <div className="flex flex-wrap gap-2 text-xs">
                                                      {issue.recommended_solution.suggested_scope && (
                                                        <Badge variant="outline" className="text-xs">
                                                          <FolderTree className="h-3 w-3 mr-1" />
                                                          {issue.recommended_solution.suggested_scope.level}
                                                          {issue.recommended_solution.suggested_scope.value && (
                                                            <>: {issue.recommended_solution.suggested_scope.value}</>
                                                          )}
                                                        </Badge>
                                                      )}
                                                      {issue.recommended_solution.estimated_impact !== undefined &&
                                                        issue.recommended_solution.estimated_impact > 0 && (
                                                        <Badge variant="secondary" className="text-xs">
                                                          <Zap className="h-3 w-3 mr-1" />
                                                          ~{issue.recommended_solution.estimated_impact} products affected
                                                        </Badge>
                                                      )}
                                                    </div>
                                                  )}
                                                </div>

                                                {/* Primary Action Button */}
                                                {savedEvaluation && (
                                                  <Button
                                                    variant={solutionInfo.buttonVariant}
                                                    size="sm"
                                                    className={`shrink-0 ${solutionInfo.buttonClass}`}
                                                    onClick={() => {
                                                      // Story 48: Open knowledge modal for domain_knowledge solutions
                                                      if (issue.recommended_solution?.type === 'domain_knowledge') {
                                                        handleOpenKnowledgeModal(issue, idx);
                                                      } else {
                                                        handleTakeAction('issue', idx, issue);
                                                      }
                                                    }}
                                                    disabled={isActionInProgress}
                                                  >
                                                    {isActionInProgress ? (
                                                      <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                                    ) : null}
                                                    {solutionInfo.label}
                                                  </Button>
                                                )}
                                              </div>
                                            </div>
                                          )}

                                          {/* Fallback: Classic action button when no recommended_solution */}
                                          {!issue.recommended_solution && !existingAction && savedEvaluation && (
                                            <div className="p-3 border-t bg-muted/50">
                                              <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-7 text-xs"
                                                onClick={() => handleTakeAction('issue', idx, issue)}
                                                disabled={isActionInProgress}
                                              >
                                                {isActionInProgress ? (
                                                  <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                                ) : null}
                                                {suggestedAction.label}
                                              </Button>
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}

                              {/* Knowledge Gaps */}
                              {analysis.KNOWLEDGE_GAPS.length > 0 && (
                                <div>
                                  <h4 className="font-medium flex items-center gap-2 mb-2">
                                    <BookOpen className="h-4 w-4 text-purple-500" />
                                    Knowledge Gaps (for future improvement)
                                  </h4>
                                  <div className="space-y-3">
                                    {analysis.KNOWLEDGE_GAPS.map((gap, idx) => (
                                      <div key={idx} className="p-3 bg-purple-50 dark:bg-purple-950/20 rounded-lg border border-purple-200 dark:border-purple-800">
                                        <div className="flex items-center gap-2 mb-1">
                                          <Badge variant="outline" className="text-xs">
                                            {gap.scope}
                                          </Badge>
                                          {gap.scopeValue && (
                                            <span className="text-xs text-muted-foreground">
                                              {gap.scopeValue}
                                            </span>
                                          )}
                                        </div>
                                        <p className="text-sm font-medium">{gap.gap}</p>
                                        <p className="text-xs text-muted-foreground mt-1">
                                          <strong>Research:</strong> {gap.researchQuestion}
                                        </p>
                                        {gap.proposedKnowledge && (
                                          <p className="text-xs text-purple-600 dark:text-purple-400 mt-1">
                                            <strong>Proposed:</strong> {gap.proposedKnowledge}
                                          </p>
                                        )}
                                      </div>
                                    ))}
                                  </div>

                                  {/* Create Proposals Button or Show Created Proposals */}
                                  {createdProposals.length > 0 ? (
                                    <div className="mt-3 p-3 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200 dark:border-green-800">
                                      <div className="flex items-center gap-2 mb-2">
                                        <CheckCircle className="h-4 w-4 text-green-600" />
                                        <span className="text-sm font-medium text-green-800 dark:text-green-200">
                                          {createdProposals.length} Proposal{createdProposals.length !== 1 ? 's' : ''} Created
                                        </span>
                                      </div>
                                      <ul className="space-y-1 mb-2">
                                        {createdProposals.map((p) => (
                                          <li key={p.id} className="text-xs text-muted-foreground truncate">
                                            {p.title}
                                          </li>
                                        ))}
                                      </ul>
                                      <a
                                        href="/domain-knowledge"
                                        className="inline-flex items-center gap-1 text-sm text-green-700 dark:text-green-300 hover:underline"
                                      >
                                        <ExternalLink className="h-3 w-3" />
                                        View in Domain Knowledge Dashboard
                                      </a>
                                    </div>
                                  ) : (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={handleCreateProposals}
                                      disabled={creatingProposals}
                                      className="mt-3"
                                    >
                                      {creatingProposals ? (
                                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                      ) : (
                                        <BookOpen className="h-4 w-4 mr-2" />
                                      )}
                                      Create Knowledge Proposals
                                    </Button>
                                  )}
                                </div>
                              )}

                              {/* Reviewer Notes */}
                              {analysis.REVIEWER_NOTES.length > 0 && (
                                <div>
                                  <h4 className="font-medium flex items-center gap-2 mb-2">
                                    <User className="h-4 w-4 text-blue-500" />
                                    Notes for Reviewer
                                  </h4>
                                  <ul className="space-y-1">
                                    {analysis.REVIEWER_NOTES.map((note, idx) => (
                                      <li key={idx} className="text-sm text-muted-foreground flex items-start gap-2">
                                        <span className="text-blue-500">&bull;</span>
                                        {note}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}

                              {/* Confidence */}
                              <div className="text-xs text-muted-foreground border-t pt-3">
                                Analysis confidence: <Badge variant="outline" className="ml-1">{analysis.CONFIDENCE}</Badge>
                              </div>
                            </div>
                          </CollapsibleContent>
                        </div>

                        {/* Re-analyze button */}
                        <div className="mt-2 flex justify-end">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleAnalyze}
                            disabled={analysisLoading}
                          >
                            <Sparkles className="h-3 w-3 mr-1" />
                            Re-analyze
                          </Button>
                        </div>
                      </Collapsible>
                    )}

                    {!analysis && !analysisLoading && !analysisError && (
                      <div className="p-6 border rounded-lg bg-muted/30 text-center">
                        <Sparkles className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                        <p className="text-sm text-muted-foreground">
                          Use AI to analyze this content for quality issues and knowledge gaps
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Hallucination Detection Section (Story 49) - Only for product_aeo */}
                {item?.contentType === 'product_aeo' && item?.id && (
                  <div className="border-t pt-4 mt-6">
                    <HallucinationReportSection
                      generationId={item.id}
                      autoRun={false}
                    />
                  </div>
                )}

                {/* Knowledge Coverage Section (Story 50) - Only for product_aeo */}
                {item?.contentType === 'product_aeo' && item?.id && (
                  <div className="border-t pt-4 mt-6">
                    <CoverageReportSection
                      generationId={item.id}
                      productId={content?.metadata?.productId as string || content?.id}
                      siteId={selectedSite?.id}
                      autoRun={false}
                    />
                  </div>
                )}

                {/* Persona Validation Section (Story 51) - Only for product_aeo */}
                {item?.contentType === 'product_aeo' && item?.id && (
                  <div className="border-t pt-4 mt-6">
                    <PersonaValidationSection
                      generationId={item.id}
                      autoRun={false}
                    />
                  </div>
                )}

                {/* Category Coherence Section (Story 52) - Only for product_aeo */}
                {item?.contentType === 'product_aeo' && item?.id && (
                  <div className="border-t pt-4 mt-6">
                    <CoherenceValidationSection
                      generationId={item.id}
                      autoRun={false}
                    />
                  </div>
                )}

                {/* Completeness Validation Section (Story 53) - Only for product_aeo */}
                {item?.contentType === 'product_aeo' && item?.id && (
                  <div className="border-t pt-4 mt-6">
                    <CompletenessValidationSection
                      generationId={item.id}
                      autoRun={false}
                    />
                  </div>
                )}
              </div>
            )}
          </ScrollArea>

          {/* Action buttons - show based on current status */}
          {item && (
            <div className="flex flex-col gap-3 mt-4 pt-4 border-t">
              {/* Status badge */}
              <div className="flex items-center gap-2 text-sm">
                <span className="text-muted-foreground">Current status:</span>
                <Badge variant={
                  item.status === 'approved' ? 'default' :
                  item.status === 'rejected' ? 'destructive' :
                  item.status === 'needs_research' ? 'warning' : 'secondary'
                }>
                  {item.status === 'approved' ? 'Approved' :
                   item.status === 'rejected' ? 'Rejected' :
                   item.status === 'needs_research' ? 'Needs Research' : 'Pending Review'}
                </Badge>
              </div>

              {/* Reviewer info for already reviewed content */}
              {item.status !== 'pending' && content?.reviewerName && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <User className="h-4 w-4" />
                  <span>
                    {item.status === 'approved' ? 'Approved' : 'Rejected'} by{' '}
                    {content.reviewerName}
                  </span>
                  {content.approvedAt && (
                    <span>
                      on {format(new Date(content.approvedAt), 'MMM d, yyyy')}
                    </span>
                  )}
                </div>
              )}

              {/* Rejection reason if rejected - prominent display with edit capability */}
              {content?.rejectionReason && (
                <div className="p-4 bg-destructive/10 rounded-lg border border-destructive/20">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
                    <div className="space-y-1 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold text-destructive">Rejection Reason</p>
                        {canEditRejection && !editingRejection && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 text-destructive/70 hover:text-destructive"
                            onClick={() => {
                              setEditedRejectionReason(content.rejectionReason || '');
                              setEditingRejection(true);
                            }}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                      {editingRejection ? (
                        <div className="space-y-2">
                          <Textarea
                            value={editedRejectionReason}
                            onChange={(e) => setEditedRejectionReason(e.target.value)}
                            className="min-h-[80px] text-sm"
                            placeholder="Enter updated rejection reason..."
                          />
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={handleEditRejectionReason}
                              disabled={editRejectionLoading || !editedRejectionReason.trim()}
                            >
                              {editRejectionLoading && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
                              Save
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setEditingRejection(false)}
                              disabled={editRejectionLoading}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <p className="text-sm text-destructive/90 max-h-[120px] overflow-y-auto">{content.rejectionReason}</p>
                      )}
                      {content.reviewerName && content.approvedAt && (
                        <p className="text-xs text-muted-foreground mt-2">
                          Rejected by {content.reviewerName} on {new Date(content.approvedAt).toLocaleString()}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Needs Research message */}
              {item.status === 'needs_research' && (
                <div className="p-3 bg-amber-50 dark:bg-amber-950/30 rounded-md border border-amber-200 dark:border-amber-800">
                  <p className="text-sm text-amber-800 dark:text-amber-200 mb-2">
                    <strong>This product needs research</strong> to find missing specification values before it can be reviewed.
                  </p>
                  <p className="text-xs text-amber-600 dark:text-amber-400">
                    Use the "Go to Research" button below to research missing specs in Enrichment Review.
                  </p>
                </div>
              )}

              {/* Action buttons */}
              <div className="flex gap-3">
                {/* Reset to Pending button - only for approved and rejected */}
                {item.status !== 'pending' && item.status !== 'needs_research' && (
                  <Button
                    variant="outline"
                    onClick={handleResetToPending}
                    disabled={actionLoading || loading}
                  >
                    <RotateCcw className="h-4 w-4 mr-2" />
                    Reset to Pending
                  </Button>
                )}

                {/* Regenerate button - only for product_aeo (not needs_research) */}
                {item.contentType === 'product_aeo' && item.status !== 'needs_research' && (
                  <RegenerationControl
                    key={`${user?.id}:${item.siteId}:${item.id}`}
                    contentId={item.id}
                    siteId={item.siteId}
                    userId={user?.id || ''}
                    disabled={actionLoading || loading || !user?.id}
                    onObserve={observeRegeneration}
                    onComplete={() => { void handleRegenerated(item.id); }}
                  />
                )}

                {/* Verify Deployment button - only for approved product_aeo */}
                {item.contentType === 'product_aeo' && item.status === 'approved' && (
                  <Button
                    variant="outline"
                    onClick={handleVerifyDeployment}
                    disabled={verifying || actionLoading || loading}
                  >
                    {verifying ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Globe className="h-4 w-4 mr-2" />
                    )}
                    Verify Deployment
                  </Button>
                )}

                {/* Verification Result Display */}
                {verificationResult && item.contentType === 'product_aeo' && item.status === 'approved' && (
                  <div className="w-full mt-4 p-4 rounded-md border bg-muted/30">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">Deployment Status</span>
                      <Badge variant={getFaqStatusColor(verificationResult.faqStatus || 'unknown')}>
                        {getFaqStatusLabel(verificationResult.faqStatus || 'unknown')}
                      </Badge>
                    </div>
                    {verificationResult.faqSimilarity !== undefined && (
                      <div className="text-sm text-muted-foreground">
                        Similarity: {Math.round(verificationResult.faqSimilarity * 100)}%
                      </div>
                    )}
                    {verificationResult.details && (
                      <div className="text-xs text-muted-foreground mt-2">
                        <span>FAQs: {verificationResult.details.matchedCount}/{verificationResult.details.ourCount} matched</span>
                        {verificationResult.details.liveCount > 0 && (
                          <span className="ml-2">({verificationResult.details.liveCount} found on page)</span>
                        )}
                      </div>
                    )}
                    {verificationResult.url && (
                      <a
                        href={verificationResult.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-blue-500 hover:underline mt-1 block truncate"
                      >
                        {verificationResult.url}
                      </a>
                    )}
                    {verificationResult.error && (
                      <div className="text-xs text-destructive mt-1">
                        Error: {verificationResult.error}
                      </div>
                    )}
                  </div>
                )}

                {/* Go to Research button - only for needs_research */}
                {item.status === 'needs_research' && item.contentType === 'product_aeo' && (
                  <Button
                    variant="default"
                    onClick={() => {
                      // Navigate to Enrichment Review with research mode
                      // Use sourceId (knowledge_base_entries ID) for research lookup, not id (aeo_page_generations ID)
                      const productId = (content as { sourceId?: string })?.sourceId || item.id;
                      window.location.href = `/research/enrichment-review?mode=research&productId=${productId}`;
                    }}
                    className="flex-1"
                  >
                    <Sparkles className="h-4 w-4 mr-2" />
                    Go to Research
                  </Button>
                )}

                {/* Reject button - available for pending and approved (not needs_research) */}
                {item.status !== 'rejected' && item.status !== 'needs_research' && (
                  <Button
                    variant="destructive"
                    onClick={() => { setPublishedRejectKind('content'); setFlagAsDataIssue(false); setRejectDialogOpen(true); }}
                    disabled={actionLoading || loading}
                    className="flex-1"
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    Reject
                  </Button>
                )}

                {/* Approve button - only for pending and rejected (not needs_research) */}
                {item.status !== 'approved' && item.status !== 'needs_research' && (
                  <Button
                    onClick={handleApprove}
                    disabled={actionLoading || loading}
                    className="flex-1"
                  >
                    {actionLoading ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <CheckCircle className="h-4 w-4 mr-2" />
                    )}
                    Approve
                  </Button>
                )}
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Rejection reason dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rejection Reason</DialogTitle>
            <DialogDescription>
              Please provide a reason for rejecting this content. This helps
              content creators improve their work.
            </DialogDescription>
          </DialogHeader>
          {/* task-52689ik88j: for product pages the panel's 'approved' IS 'published' — the queue
              normalises published → approved (ContentReviewService getQueue) — and a plain reject
              is refused on a published page by design (GH #3214). Say so BEFORE the reviewer
              types a reason: the server used to answer the refusal with "changed concurrently —
              reload and try again", which sent a client reviewer into three retries in 26 seconds
              and a support ticket saying the button does not work. */}
          {item?.contentType === 'product_aeo' && item?.status === 'approved' && (
            <div
              data-testid="reject-published-notice"
              className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
            >
              This page is published. A plain reject is refused on published content. Choose below
              why you are rejecting it: “Content problem, regenerate” pulls the page back to review
              for regeneration and records nothing against the product; “Data quality issue” pulls
              it back and also records a data issue against the product.
            </div>
          )}
          <Textarea
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            placeholder="Please explain why this content is being rejected..."
            className="min-h-[100px]"
          />
          {/* task-edbnz2qj5c: a PUBLISHED product page chooses between the two pull-backs. Native
              radios (not the Radix group) so the choice is a plain form control the tests and the
              reviewer's keyboard both reach. Default 'content': the common case after this change
              is a text problem on a correct product, and the false data issue was the defect. */}
          {item?.contentType === 'product_aeo' && item?.status === 'approved' && (
            <fieldset data-testid="reject-published-kind" className="space-y-2 py-2">
              <legend className="text-sm font-medium">Why are you rejecting this published page?</legend>
              <div className="flex items-start space-x-2">
                <input
                  type="radio"
                  id="rejectKindContent"
                  name="publishedRejectKind"
                  value="content"
                  checked={publishedRejectKind === 'content'}
                  onChange={() => setPublishedRejectKind('content')}
                  className="mt-1 h-4 w-4 border-gray-300"
                />
                <label htmlFor="rejectKindContent" className="text-sm text-muted-foreground">
                  Content problem, regenerate (the generated text is wrong; the product data is right — no data issue is recorded)
                </label>
              </div>
              <div className="flex items-start space-x-2">
                <input
                  type="radio"
                  id="rejectKindDataQuality"
                  name="publishedRejectKind"
                  value="data_quality"
                  checked={publishedRejectKind === 'data_quality'}
                  onChange={() => setPublishedRejectKind('data_quality')}
                  className="mt-1 h-4 w-4 border-gray-300"
                />
                <label htmlFor="rejectKindDataQuality" className="text-sm text-muted-foreground">
                  Data quality issue (wrong feed data — a data issue is recorded against the product)
                </label>
              </div>
            </fieldset>
          )}
          {/* GH #3214: the data-quality flag only applies to product AEO content. Hiding
              it for other types keeps the partial-success path (reject applied but flag
              dropped) unreachable from the UI; the backend also rejects it with 400.
              task-edbnz2qj5c: a published page uses the choice above instead. */}
          {item?.contentType === 'product_aeo' && item?.status !== 'approved' && (
            <div className="flex items-center space-x-2 py-2">
              <input
                type="checkbox"
                id="flagAsDataIssue"
                checked={flagAsDataIssue}
                onChange={(e) => setFlagAsDataIssue(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300"
              />
              <label htmlFor="flagAsDataIssue" className="text-sm text-muted-foreground">
                Flag as Data Quality Issue (wrong feed data, not content generation problem)
              </label>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRejectDialogOpen(false)}
              disabled={actionLoading}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleReject}
              disabled={!rejectionReason.trim() || actionLoading}
            >
              {actionLoading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <XCircle className="h-4 w-4 mr-2" />
              )}
              Confirm Rejection
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Story 48: Knowledge creation from issue modal */}
      {knowledgeModalIssue && content && (
        <KnowledgeFromIssueModal
          open={knowledgeModalOpen}
          onClose={() => {
            setKnowledgeModalOpen(false);
            setKnowledgeModalIssue(null);
          }}
          issue={knowledgeModalIssue}
          issueIndex={knowledgeModalIssueIndex}
          productContext={{
            id: content.id,
            title: content.title,
            category_path: content.categoryPath as string | undefined,
            brand: content.brand as string | undefined,
            contentType: content.contentType,
          }}
          evaluationId={savedEvaluation?.id}
          onCreated={handleKnowledgeCreated}
        />
      )}
    </>
  );
}

export default ReviewPanel;
